import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';

const TotalBalanceCardContainer = styled.div`
  border-radius: ${variables.borderRadius.borderRadiusMdPlus};
  background-color: ${themeColors.colorBackgroundPrimary};
  box-shadow: 0px 3px 9px 0px ${themeColors.colorShadowCard};
  border: 1px solid ${themeColors.colorWhite};
  padding: ${pxToRem(24)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: ${pxToRemMd(20)};
  }
`;

const TotalBalanceTitle = styled.p`
  display: block;
  font-family: ${variables.family.goldSilver};
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0;
  color: ${themeColors.colorTextSecondary};
  margin: 0 0 ${pxToRem(8)};
`;

const TotalBalanceAmount = styled.p`
  display: block;
  font-family: ${variables.family.goldSilver};
  font-size: 24px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0;
  color: ${themeColors.colorTextPrimary};
  margin: 0 0 ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: 500;
  }
`;

const BalanceProgressTrack = styled.div`
  display: flex;
  width: 100%;
  height: 8px;
  border-radius: ${variables.borderRadius.borderRadiusCapsule};
  overflow: hidden;
  margin-bottom: ${pxToRem(16)};
`;

const BalanceProgressSegment = styled.div`
  height: 100%;
  width: ${({ percent }) => percent}%;
  background-color: ${({ segmentColor }) => segmentColor};
`;

const BalanceLegendRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${pxToRem(24)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    gap: ${pxToRemMd(16)};
  }
`;

const BalanceLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
`;

const BalanceLegendDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ dotColor }) => dotColor};
`;

const BalanceLegendLabel = styled.span`
  display: block;
  font-family: ${variables.family.goldSilver};
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  text-align: center;
  color: ${themeColors.colorTextSecondary};
`;

const TotalBalanceCard = ({
  title,
  availableLabel,
  reservedLabel,
  totalBalance,
  availableBalance,
  reservedBalance,
}) => {
  const availablePercent =
    totalBalance > 0 ? (availableBalance / totalBalance) * 100 : 0;
  const reservedPercent =
    totalBalance > 0 ? (reservedBalance / totalBalance) * 100 : 0;

  return (
    <TotalBalanceCardContainer>
      <TotalBalanceTitle>{title}</TotalBalanceTitle>
      <TotalBalanceAmount>
        {formatMoneyNumeral(totalBalance)}
      </TotalBalanceAmount>
      <BalanceProgressTrack>
        <BalanceProgressSegment
          percent={availablePercent}
          segmentColor={themeColors.colorBalanceAvailable}
        />
        <BalanceProgressSegment
          percent={reservedPercent}
          segmentColor={themeColors.colorBalanceReserved}
        />
      </BalanceProgressTrack>
      <BalanceLegendRow>
        <BalanceLegendItem>
          <BalanceLegendDot dotColor={themeColors.colorBalanceAvailable} />
          <BalanceLegendLabel>{availableLabel}</BalanceLegendLabel>
        </BalanceLegendItem>
        <BalanceLegendItem>
          <BalanceLegendDot dotColor={themeColors.colorBalanceReserved} />
          <BalanceLegendLabel>{reservedLabel}</BalanceLegendLabel>
        </BalanceLegendItem>
      </BalanceLegendRow>
    </TotalBalanceCardContainer>
  );
};

TotalBalanceCard.propTypes = {
  title: PropTypes.string.isRequired,
  availableLabel: PropTypes.string.isRequired,
  reservedLabel: PropTypes.string.isRequired,
  totalBalance: PropTypes.number,
  availableBalance: PropTypes.number,
  reservedBalance: PropTypes.number,
};

TotalBalanceCard.defaultProps = {
  totalBalance: 0,
  availableBalance: 0,
  reservedBalance: 0,
};

export default TotalBalanceCard;
